import { createStore, createEffect, createEvent, sample, combine } from 'effector'

import { makeApiCall } from 'app/effector/api'
import { $features } from 'app/effector/features'

import { fetchNomineesTransferProviders, NomineesTransferProviderRegulatoryType } from './api'
import { NomineesTransferProviderList, NomineesTransferProvider } from './models'

// events
const fetchProvidersIfNeeded = createEvent<NomineesTransferProviderRegulatoryType>()
const providersReceived = createEvent()

// effects
const fetchNomineesTransferProvidersFx = createEffect(
  async (regulatoryType?: NomineesTransferProviderRegulatoryType) =>
    await makeApiCall(fetchNomineesTransferProviders, regulatoryType),
)

// stores
const $nomineesTransferProviderList = createStore(new NomineesTransferProviderList())

const $sippProviders = $nomineesTransferProviderList.map((providers) =>
  providers.filter((provider) => provider.regulatory_type === NomineesTransferProviderRegulatoryType.SIPP),
)

const $vanguardProviders = $sippProviders.map((providers) =>
  providers.filter((provider) => (provider.name ?? '').toLowerCase().includes('vanguard')),
)

const $baseProviders = combine(
  $features,
  $sippProviders,
  $vanguardProviders,
  (features, sippProviders, vanguardProviders) => {
    const providersToDisplay = features.get('multiple-sipp-providers-transfer') ? sippProviders : vanguardProviders

    return providersToDisplay
  },
)

const $skeletonProviders = $features.map((features) => {
  const multipleProviders = features.get('multiple-sipp-providers-transfer')

  const skeleton = {
    ...new NomineesTransferProvider({
      name: multipleProviders ? '██████ ██████' : '█████',
    }),
    skeleton: true,
  }

  return multipleProviders ? [skeleton, skeleton] : [skeleton]
})

// Logic
$nomineesTransferProviderList.on(fetchNomineesTransferProvidersFx.doneData, (state, providers) =>
  state.merge(new NomineesTransferProviderList(...providers)),
)

// Fetch providers when needed
sample({
  clock: fetchProvidersIfNeeded,
  source: $nomineesTransferProviderList,
  filter: (providers, regulatoryType) => {
    const providersOfRegulatoryType = providers.filter((provider) => provider.regulatory_type === regulatoryType)

    return providersOfRegulatoryType.length < 1
  },
  fn: (_, regulatoryType) => regulatoryType,
  target: fetchNomineesTransferProvidersFx,
})

// Trigger providersReceived when providers are fetched
sample({
  clock: $nomineesTransferProviderList,
  filter: (providerList) => providerList.length > 0,
  target: providersReceived,
})

export * from './api'
export * from './models'

export {
  fetchProvidersIfNeeded,
  providersReceived,
  fetchNomineesTransferProvidersFx,
  $nomineesTransferProviderList,
  $baseProviders,
  $skeletonProviders,
}
